/**
 * Persists linked third-party accounts (currently GitHub) under
 * AppLocalData/app-settings/accounts.json. Browser dev falls back to localStorage.
 *
 * NOTE: the access token is stored at rest in app data. Acceptable for the
 * desktop MVP; a future hardening pass should move it to the OS keychain.
 */
import { prefersFilesystemStorage } from './localWorkspaceStore';
import { addSessionEndedListener } from './channelService';
import { LOCAL_NAMESPACE } from './storageNamespace';
import { gitAccountSpace } from './git/gitAccountSpace';

const ACCOUNTS_DIR = 'app-settings';
const ACCOUNTS_FILE = 'app-settings/accounts.json';
const BROWSER_KEY = 'apiboo-accounts';

const EMPTY = { version: 1, github: null, workspaceGitLinks: {} };

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

function readBrowser() {
  try {
    const raw = localStorage.getItem(BROWSER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function readFs() {
  try {
    const fs = await fsModule();
    const text = await fs.readTextFile(ACCOUNTS_FILE, {
      baseDir: fs.BaseDirectory.AppLocalData,
    });
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function loadAccounts() {
  let data = null;
  if (await prefersFilesystemStorage()) {
    data = await readFs();
  }
  if (!data) data = readBrowser();
  return { ...EMPTY, ...(data || {}) };
}

export async function saveAccounts(accounts) {
  const payload = JSON.stringify({ ...EMPTY, ...(accounts || {}) });
  try {
    localStorage.setItem(BROWSER_KEY, payload);
  } catch {
    // ignore
  }
  if (await prefersFilesystemStorage()) {
    try {
      const fs = await fsModule();
      await fs.mkdir(ACCOUNTS_DIR, {
        baseDir: fs.BaseDirectory.AppLocalData,
        recursive: true,
      });
      await fs.writeTextFile(ACCOUNTS_FILE, payload, {
        baseDir: fs.BaseDirectory.AppLocalData,
      });
    } catch (err) {
      console.warn('[accountsStore] Failed to persist accounts to disk:', err);
    }
  }
}

export async function getGitHubAccount() {
  const accounts = await loadAccounts();
  return accounts.github || null;
}

export async function setGitHubAccount(account) {
  const accounts = await loadAccounts();
  accounts.github = account;
  await saveAccounts(accounts);
  return account;
}

export async function clearGitHubAccount() {
  const accounts = await loadAccounts();
  accounts.github = null;
  await saveAccounts(accounts);
}

/**
 * Wipe every locally-linked account and remote link. Called on sign-out so a
 * persisted GitHub repo token never survives a user switch on a shared install.
 *
 * Except a workspace's LOCAL-ONLY git link (`provider: 'local'`, 04.10.2026):
 * it holds no account, URL or credential — only "this workspace's history is
 * in its mirror folder on this device" — and the sign-out keeps that history
 * on "Keep on this device". Dropping the link would orphan it.
 */
export async function clearLinkedAccounts() {
  let kept = {};
  let keptScopes = {};
  let keptGit = [];
  try {
    const accounts = await loadAccounts();
    // The signed-out space's git accounts (GitLab, Gitea, GitHub by token —
    // owner 07.10.2026) belong to the local root, not to the account that is
    // leaving: they stay, with every link that uses them.
    keptGit = (accounts.gitAccounts || []).filter((a) => gitAccountSpace(a) === LOCAL_NAMESPACE);
    const localAccountIds = new Set(keptGit.map((a) => a.id));
    const stays = (links) =>
      Object.fromEntries(
        Object.entries(links || {}).filter(
          ([, git]) => git?.provider === 'local' || (git?.accountId && localAccountIds.has(git.accountId))
        )
      );
    kept = stays(accounts.workspaceGitLinks);
    // T10: "My projects, boards & documents" tracked on this device — same rule (its key names the account).
    keptScopes = stays(accounts.scopeGitLinks);
  } catch {
    kept = {};
    keptScopes = {};
    keptGit = [];
  }
  await saveAccounts({
    ...EMPTY,
    workspaceGitLinks: kept,
    ...(Object.keys(keptScopes).length > 0 ? { scopeGitLinks: keptScopes } : {}),
    ...(keptGit.length > 0 ? { gitAccounts: keptGit } : {}),
  });
}

// This module has no module-level cache (every call re-reads disk/
// localStorage), but clearLinkedAccounts() itself is still session-derived
// cleanup that used to depend on every logout call site remembering to call
// it — it was reached on the hard path (authStore.logout()) and, separately,
// duplicated into the generic auth:logout listener so the soft path got it
// too; two call sites doing the same thing is exactly the shape that let
// J5 (soft logout leaving a departed GitHub token behind) happen in the
// first place. Subscribing here means this module resets itself on every
// session end, and no teardown call site needs to remember it exists.
addSessionEndedListener(() => {
  clearLinkedAccounts().catch((e) =>
    console.warn('[accountsStore] Failed to clear linked accounts on session end:', e)
  );
});

/* ── Per-scope git remote links (git sync Stage 2: boards / documents / projects) ── */

export async function getAllScopeGitLinks() {
  const accounts = await loadAccounts();
  return accounts.scopeGitLinks || {};
}

export async function setScopeGitLink(scopeKey, git) {
  const accounts = await loadAccounts();
  accounts.scopeGitLinks = { ...(accounts.scopeGitLinks || {}) };
  if (git) accounts.scopeGitLinks[String(scopeKey)] = git;
  else delete accounts.scopeGitLinks[String(scopeKey)];
  await saveAccounts(accounts);
}

/* ── Per-workspace git remote links (works for any workspace type) ──────────── */

export async function getAllWorkspaceGitLinks() {
  const accounts = await loadAccounts();
  return accounts.workspaceGitLinks || {};
}

export async function setWorkspaceGitLink(workspaceId, git) {
  const accounts = await loadAccounts();
  accounts.workspaceGitLinks = { ...(accounts.workspaceGitLinks || {}) };
  if (git) {
    accounts.workspaceGitLinks[String(workspaceId)] = git;
  } else {
    delete accounts.workspaceGitLinks[String(workspaceId)];
  }
  await saveAccounts(accounts);
}
